import { Logger } from "../../tools/Logger";
import { store } from "../../app/store";
import {
  onEvent,
  setTelemetry,
  setUavNotifications,
  telemetryNotify,
  telemetryAbort,
  telemetryStale,
} from "./aircraftSlice";
import { SingleSocket } from "./SingleSocket";
import { Timer } from "./Timer";
import { isDirectTelemetry, isTelemetry } from "./types";
import { missionGcsThunk, setUavMission } from "../mission/missionSlice";
import { selectClientId } from "./aircraftSelectors";
import { selectUsername } from "../session/sessionSlice";
export class WebSocketPair {
  private TAG: string = "WebSocketPair";
  private ws1: SingleSocket | null;
  private ws2: SingleSocket | null;
  private notifyTimer: Timer | null;
  private staleTimer: Timer | null;
  private abortTimer: Timer | null;
  private lastMessage: number = 0;
  private ip1: string;
  private ip2: string;
  private token: string;
  private uavId: string;
  private firstTelemetry: boolean = false;
  private lastEvent: string = "";
  private missionId: string = "";
  private clientId: string;
  private user: string = "";

  constructor(
    ip1: string,
    ip2: string,
    token: string,
    uavId: string,
    port: string
  ) {
    this.ip1 = ip1;
    this.ip2 = ip2;
    this.token = token;
    this.uavId = uavId;
    this.clientId = selectClientId(store.getState());
    this.user = selectUsername(store.getState()) || "";
    this.ws1 = new SingleSocket(
      this.ip1,
      this.token,
      this.onmessage(0),
      this.uavId,
      port,
      0
    );
    this.ws2 = new SingleSocket(
      this.ip2,
      this.token,
      this.onmessage(1),
      this.uavId,
      port,
      1
    );
    this.notifyTimer = new Timer(10000, this.onNotify);
    this.staleTimer = new Timer(15000, this.onStale);
    this.abortTimer = new Timer(45000, this.onAbort);
  }
  onmessage = (index: 0 | 1) => (event: MessageEvent) => {
    const data = JSON.parse(event?.data);
    if (isTelemetry(data)) {
      Logger.log("info", this.TAG, "telemetry not handled", this.getLogData());
    } else if (isDirectTelemetry(data)) {
      if (this.firstTelemetry === false) {
        this.firstTelemetry = true;
        store.dispatch(
          setUavNotifications({
            uavId: this.uavId,
            notification: {
              msg: "Telemetry Connected",
              type: "TELEMETRY",
              action: ["TELEMETRY"],
              level: 2,
              timestamp: Date.now(),
              info: true,
            },
          })
        );
      }
      this.restartTimers();
      // @ts-ignore
      if (data.payload.uav_time > this.lastMessage) {
        // @ts-ignore
        this.lastMessage = data.payload.uav_time;
        store.dispatch(setTelemetry({ telemetry: data, simIndex: index }));
      } else {
      }
    } else if (data.type === "EVENT") {
      Logger.log(
        "debug",
        this.TAG,
        `telemetry: ${data?.event}`,
        this.getLogData()
      );

      this.lastEvent = data.event;

      store.dispatch(onEvent({ uavId: this.uavId, event: data.event }));

      if (data.event === "MISSION_STARTED") {
        setTimeout(() => {
          if (this.lastEvent !== "MISSION_STARTED") return;
          store.dispatch(
            onEvent({ uavId: this.uavId, event: "MISSION_IN_FLIGHT" })
          );
        }, 10000);
      }
    } else if (data.geoJsonLine) {
      try {
        const geoJson = JSON.parse(data.geoJsonLine);
        store.dispatch(
          setUavMission({
            uavId: this.uavId,
            mission: {
              ...data,
              geoJson: geoJson,
            },
          })
        );
        store.dispatch(missionGcsThunk({ missionId: data.id, uavId: this.uavId }));
      } catch (err: any) {
        Logger.log("error", this.TAG, err?.message, {
          ...this.getLogData(),
          err,
        });
      }
    } else {
      Logger.error(this.TAG + ` ${this.uavId}`, `telemetry type not defined`);
      console.log(data);
    }
  };
  public sendToIndex = (data: string, simIndex: 0 | 1) => {
    if (simIndex === 0) {
      this.ws1?.send(data);
    } else {
      this.ws2?.send(data);
    }
  };
  public send = (data: string) => {
    if (this.ws1?.getReadyState() === 1) {
      this.ws1?.send(data);
    } else if (this.ws2?.getReadyState() === 1) {
      this.ws2?.send(data);
    }
  };
  public getLogData = () => {
    return {
      uavId: this.uavId,
      uav_id: this.uavId,
      ip: `${this.ip1} ${this.ip2}`,
      clientId: this.clientId,
      user: this.user,
    };
  };
  private onNotify = () => {
    Logger.debug(this.TAG + ` ${this.uavId}`, `onNotify`);
    store.dispatch(telemetryNotify({ uavId: this.uavId }));
    this.notifyTimer?.restart();
  };
  private onStale = () => {
    Logger.debug(this.TAG + ` ${this.uavId}`, `onStale`);
    store.dispatch(telemetryStale({ uavId: this.uavId }));
  };
  private onAbort = () => {
    Logger.debug(this.TAG + ` ${this.uavId}`, `onAbort`);
    store.dispatch(telemetryAbort({ uavId: this.uavId }));
  };
  restartTimers = () => {
    this.notifyTimer?.restart();
    this.staleTimer?.restart();
    this.abortTimer?.restart();
  };
  public destroyConnection() {
    Logger.info(this.TAG, `destroyConnections`, this.getLogData());
    try {
      if (this.ws1) {
        this.ws1.destroyConnection();
        this.ws1 = null;
      }
    } catch (err: any) {
      Logger.log("error", this.TAG, err?.message, {
        ...this.getLogData(),
        err,
      });
    }
    try {
      if (this.ws2) {
        this.ws2.destroyConnection();
        this.ws2 = null;
      }
    } catch (err: any) {
      Logger.log("error", this.TAG, err?.message, {
        ...this.getLogData(),
        err,
      });
    }
    try {
      if (this.notifyTimer) {
        this.notifyTimer.kill();
        this.notifyTimer = null;
      }
    } catch (err: any) {
      Logger.log("error", this.TAG, err?.message, {
        ...this.getLogData(),
        err,
      });
    }
    try {
      if (this.staleTimer) {
        this.staleTimer.kill();
        this.staleTimer = null;
      }
    } catch (err: any) {
      Logger.log("error", this.TAG, err?.message, {
        ...this.getLogData(),
        err,
      });
    }
    try {
      if (this.abortTimer) {
        this.abortTimer.kill();
        this.abortTimer = null;
      }
    } catch (err: any) {
      Logger.log("error", this.TAG, err?.message, {
        ...this.getLogData(),
        err,
      });
    }
  }
}
